import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTrack } from "@/contexts/track";
import { useAuth } from "@/contexts/auth";
import { useLanguage } from "@/contexts/language";
import { fetchWithAuth } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Trophy, Crown } from "lucide-react";
import type { TranslationDict } from "@/i18n/en";

export type LeaderboardPeriod = "week" | "month" | "all";

interface LeaderboardEntry {
  rank: number;
  userId: number;
  nickname: string;
  /** Ranking score used for ordering (higher is better). */
  rankScore: number;
  correctCount: number;
  wrongCount: number;
  answeredCount: number;
  accuracyPercent: number;
}

interface LeaderboardResponse {
  period: LeaderboardPeriod;
  track: string;
  metric: string;
  metricFormula?: string;
  entries: LeaderboardEntry[];
  myRank: LeaderboardEntry | null;
}

type LeaderboardCopy = TranslationDict["leaderboard"];

async function fetchLeaderboard(track: string, period: LeaderboardPeriod): Promise<LeaderboardResponse> {
  return fetchWithAuth<LeaderboardResponse>(`/api/stats/leaderboard?track=${encodeURIComponent(track)}&period=${encodeURIComponent(period)}`);
}

function accuracyPct(row: LeaderboardEntry): string {
  if (row.answeredCount <= 0) return "—";
  return `${Math.round(row.accuracyPercent)}%`;
}

function PodiumSlot({
  row,
  place,
  isYou,
  L,
  className,
}: {
  row: LeaderboardEntry | undefined;
  place: 1 | 2 | 3;
  isYou: boolean;
  L: LeaderboardCopy;
  className?: string;
}) {
  const heights =
    place === 1
      ? "min-h-[132px] pt-6"
      : place === 2
        ? "min-h-[108px] pt-5"
        : "min-h-[96px] pt-4";

  const border =
    place === 1
      ? "border-amber-400/50 bg-gradient-to-b from-amber-500/15 to-card/90 shadow-[0_0_24px_-8px_rgba(251,191,36,0.35)]"
      : place === 2
        ? "border-slate-400/35 bg-gradient-to-b from-slate-400/10 to-card/80"
        : "border-amber-800/40 bg-gradient-to-b from-amber-900/20 to-card/80";

  const medal =
    place === 1 ? (
      <Crown className="h-5 w-5 text-amber-400 drop-shadow-sm" aria-hidden />
    ) : (
      <span
        className={cn(
          "flex h-7 w-7 items-center justify-center rounded-full text-xs font-black tabular-nums",
          place === 2 && "bg-slate-300/90 text-slate-900",
          place === 3 && "bg-amber-800/90 text-amber-100",
        )}
        aria-hidden
      >
        {place}
      </span>
    );

  return (
    <div
      className={cn(
        "flex flex-1 flex-col rounded-xl border px-2 pb-3 text-center transition-colors",
        heights,
        border,
        !row && "border-dashed border-border/50 bg-muted/20 opacity-60",
        className,
      )}
    >
      <div className="mb-2 flex flex-col items-center gap-1">
        {medal}
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          {place === 1 ? L.podium1st : place === 2 ? L.podium2nd : L.podium3rd}
        </span>
      </div>
      {row ? (
        <>
          <p className={cn("truncate px-0.5 text-sm font-semibold text-foreground", isYou && "text-primary")} title={row.nickname}>
            {row.nickname}
            {isYou ? <span className="ml-1 text-[10px] font-normal text-primary">({L.you})</span> : null}
          </p>
          <p className="mt-1 font-mono text-lg font-bold tabular-nums text-foreground">{row.rankScore}</p>
          <p className="mt-0.5 font-mono text-[10px] tabular-nums text-muted-foreground leading-tight">
            {row.correctCount}✓ · {row.wrongCount}✗ · {accuracyPct(row)}
          </p>
          <p className="mt-0.5 font-mono text-[9px] text-muted-foreground/70">{L.attemptsLabel}: {row.answeredCount}</p>
        </>
      ) : (
        <p className="mt-auto font-mono text-[10px] text-muted-foreground">—</p>
      )}
    </div>
  );
}

/** 2nd – 1st – 3rd left to right, baseline aligned */
function LeaderboardPodium({
  entries,
  currentUserId,
  L,
}: {
  entries: LeaderboardEntry[];
  currentUserId: number | undefined;
  L: LeaderboardCopy;
}) {
  const p1 = entries.find((e) => e.rank === 1);
  const p2 = entries.find((e) => e.rank === 2);
  const p3 = entries.find((e) => e.rank === 3);
  if (!p1 && !p2 && !p3) return null;

  const count = entries.length;
  if (count === 1 && p1) {
    return (
      <div className="mb-4">
        <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{L.podiumTitle}</p>
        <div className="mx-auto max-w-[200px]">
          <PodiumSlot row={p1} place={1} isYou={currentUserId === p1.userId} L={L} />
        </div>
      </div>
    );
  }

  if (count === 2) {
    const left = p2 ?? p1;
    const right = p2 ? p1 : p3;
    const leftIsSecond = !!p2;
    return (
      <div className="mb-4">
        <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{L.podiumTitle}</p>
        <div className="flex items-end justify-center gap-3">
          <PodiumSlot
            row={left}
            place={leftIsSecond ? 2 : 1}
            isYou={currentUserId === left?.userId}
            L={L}
            className="max-w-[140px]"
          />
          <PodiumSlot
            row={right}
            place={leftIsSecond ? 1 : 2}
            isYou={currentUserId === right?.userId}
            L={L}
            className="max-w-[140px]"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4">
      <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{L.podiumTitle}</p>
      <div className="flex items-end justify-center gap-2 sm:gap-3">
        <PodiumSlot row={p2} place={2} isYou={currentUserId === p2?.userId} L={L} className="max-w-[120px] sm:max-w-[140px]" />
        <PodiumSlot row={p1} place={1} isYou={currentUserId === p1?.userId} L={L} className="max-w-[130px] sm:max-w-[150px] scale-[1.02] z-[1]" />
        <PodiumSlot row={p3} place={3} isYou={currentUserId === p3?.userId} L={L} className="max-w-[120px] sm:max-w-[140px]" />
      </div>
    </div>
  );
}

export function LeaderboardSheetBody() {
  const { selectedTrack } = useTrack();
  const { user } = useAuth();
  const { t } = useLanguage();
  const L = t.leaderboard;

  const [period, setPeriod] = useState<LeaderboardPeriod>("week");
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["leaderboard", selectedTrack, period],
    queryFn: () => fetchLeaderboard(selectedTrack, period),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div>
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" aria-hidden />
          <h2 className="text-lg font-bold tracking-tight text-foreground">{L.title}</h2>
        </div>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{L.subtitle}</p>
        <p className="mt-1 font-mono text-[10px] text-muted-foreground/80">{L.metricHelp}</p>
        <p className="mt-1 font-mono text-[10px] text-muted-foreground/70">{L.sortHint}</p>
      </div>

      <Tabs value={period} onValueChange={(v) => { setPeriod(v as LeaderboardPeriod); }} className="flex min-h-0 flex-1 flex-col gap-3">
        <TabsList className="grid w-full grid-cols-3 font-mono text-[10px] uppercase tracking-wider sm:text-xs">
          <TabsTrigger value="week">{L.tabWeek}</TabsTrigger>
          <TabsTrigger value="month">{L.tabMonth}</TabsTrigger>
          <TabsTrigger value="all">{L.tabAll}</TabsTrigger>
        </TabsList>

        {(["week", "month", "all"] as const).map((p) => (
          <TabsContent key={p} value={p} className="mt-0 flex min-h-0 flex-1 flex-col data-[state=inactive]:hidden">
            {/*
              Radix may keep inactive tab content mounted. Only the *selected* period owns the query
              `data`; when switching tabs, `data` can be undefined while refetching — never read
              `data.entries` unless this panel is the active period.
            */}
            {period !== p ? null : isLoading ? (
              <div className="space-y-2 py-2">
                <Skeleton className="h-32 w-full rounded-xl" />
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-11 w-full rounded-md" />
                ))}
              </div>
            ) : isError ? (
              <div className="py-8 text-center font-mono text-sm text-destructive">
                {L.loadError}
                <button type="button" onClick={() => refetch()} className="mt-2 block w-full text-xs text-primary underline">
                  {L.retry}
                </button>
              </div>
            ) : !data ? (
              <div className="space-y-2 py-2">
                <Skeleton className="h-32 w-full rounded-xl" />
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-11 w-full rounded-md" />
                ))}
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-3">
                {(data.entries ?? []).length === 0 ? (
                  <div className="rounded-lg border border-dashed border-border py-8 text-center font-mono text-sm text-muted-foreground">
                    {L.empty}
                  </div>
                ) : (
                  <>
                    <LeaderboardPodium entries={data.entries ?? []} currentUserId={user?.id} L={L} />

                    {(data.entries ?? []).length > 3 ? (
                      <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-border bg-card/30">
                        <p className="border-b border-border/80 bg-muted/40 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                          {L.fullStandings}
                        </p>
                        <table className="w-full text-left text-[11px] font-mono sm:text-xs">
                          <thead className="sticky top-0 z-[1] border-b border-border bg-muted/90 backdrop-blur-sm">
                            <tr className="text-[10px] uppercase tracking-widest text-muted-foreground">
                              <th className="w-9 px-2 py-2 text-center">{L.colRank}</th>
                              <th className="min-w-0 px-2 py-2">{L.colPilot}</th>
                              <th className="w-12 px-2 py-2 text-right">{L.colRankScore}</th>
                              <th className="w-10 px-2 py-2 text-right">{L.colCorrect}</th>
                              <th className="hidden w-10 px-2 py-2 text-right sm:table-cell">{L.colWrong}</th>
                              <th className="w-11 px-2 py-2 text-right">{L.colAccuracy}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(data.entries ?? []).slice(3).map((row) => {
                              const isYou = user?.id === row.userId;
                              return (
                                <tr
                                  key={row.userId}
                                  className={cn(
                                    "border-b border-border/40 last:border-0",
                                    isYou && "bg-primary/10",
                                  )}
                                >
                                  <td className="px-2 py-2.5 text-center font-bold tabular-nums text-muted-foreground">{row.rank}</td>
                                  <td className="max-w-[100px] truncate px-2 py-2.5 font-medium text-foreground sm:max-w-[160px]" title={row.nickname}>
                                    {row.nickname}
                                    {isYou ? <span className="ml-1 text-[10px] text-primary">({L.you})</span> : null}
                                  </td>
                                  <td className="px-2 py-2.5 text-right font-semibold tabular-nums text-foreground">{row.rankScore}</td>
                                  <td className="px-2 py-2.5 text-right tabular-nums text-muted-foreground">{row.correctCount}</td>
                                  <td className="hidden px-2 py-2.5 text-right tabular-nums text-muted-foreground sm:table-cell">{row.wrongCount}</td>
                                  <td className="px-2 py-2.5 text-right tabular-nums text-muted-foreground">{accuracyPct(row)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                  </>
                )}

                <div
                  className={cn(
                    "shrink-0 rounded-xl border p-4",
                    data.myRank ? "border-primary/35 bg-primary/[0.07]" : "border-border bg-card/60",
                  )}
                >
                  <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{L.yourRank}</p>
                  {data.myRank ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                      <div className="font-mono text-sm">
                        <span className="text-2xl font-bold text-foreground">#{data.myRank.rank}</span>
                        <span className="text-muted-foreground"> · {data.myRank.nickname}</span>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          <span className="font-semibold text-primary">{L.colRankScore}</span>{" "}
                          <span className="text-foreground font-bold">{data.myRank.rankScore}</span>
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-muted-foreground">
                        <span>
                          <span className="font-semibold text-foreground">{data.myRank.correctCount}</span> {L.correctLabel}
                        </span>
                        <span>
                          <span className="font-semibold text-foreground">{data.myRank.wrongCount}</span> {L.wrongLabel}
                        </span>
                        <span>
                          <span className="font-semibold text-foreground">{data.myRank.answeredCount}</span> {L.attemptsLabel}
                        </span>
                        <span>
                          <span className="font-semibold text-foreground">{accuracyPct(data.myRank)}</span> {L.accuracyLabel}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs leading-snug text-muted-foreground">{L.notRanked}</p>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
